import { useFlag } from '@calendly/feature-check-react';
import { useFormContext } from '@calendly/form';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { AlertWarningCircleIcon, AlertWarningTriangleIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorIconWarning, colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { forwardRef } from 'react';
import { LocationIcon } from 'components/core/location_icon';
import { infoForLocation } from 'components/event_type_edit';
import { useEventType } from 'features/event_type_provider';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { useLocationConfigurations } from './hooks/use_location_configurations';
const locationSummaryContainerCss = "lf5c6h6";
const LocationSummaryContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(locationSummaryContainerCss, props.className)} />;
});
const locationTipDangerCss = "lpdvbu6";
const locationTipCss = "lcm1ju5";
const LocationTip = forwardRef((props, ref) => {
  const {
    danger,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(locationTipCss, {
    [locationTipDangerCss]: danger
  }, props.className)} />;
});
const iconWrapperCss = "i1qlod0l";
const IconWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(iconWrapperCss, props.className)} />;
});
const getLabelText = kind => ({
  google_conference: I18n.t('publisher.event_types.location.google_conference.label'),
  gotomeeting_conference: I18n.t('publisher.event_types.location.gotomeeting_conference.label'),
  zoom_conference: I18n.t('publisher.event_types.location.zoom_conference.label'),
  microsoft_teams_conference: I18n.t('publisher.event_types.location.microsoft_teams_conference.label'),
  webex_conference: I18n.t('publisher.event_types.location.webex_conference.label')
})[kind];
const EmptyLocationSummary = () => <LocationSummaryContainer>
    <LocationTip>
      <AlertWarningCircleIcon spacingRight="8" />
      {HtmlParser(I18n.t('publisher.event_type_editor.sections.event_details.location.empty_tip'))}
    </LocationTip>
  </LocationSummaryContainer>;
const EmptyLocationSummaryUser = () => <LocationSummaryContainer>
    <BodyText color={colorTextSecondary}>
      <AlertWarningCircleIcon width={16} color={colorIconWarning} spacingRight="8" />
      {I18n.t('publisher.event_types.location.no_location_set')}
    </BodyText>
  </LocationSummaryContainer>;
const LocationErrorSummary = ({
  kind
}) => <LocationSummaryContainer>
    <LocationTip danger>
      <AlertWarningTriangleIcon spacingRight="8" />
      {I18n.t('publisher.event_type_editor.sections.event_details.location.connection_error', {
      kind: getLabelText(kind)
    })}
    </LocationTip>
  </LocationSummaryContainer>;
const NoLocationReadOnlySummary = () => <LocationSummaryContainer>
    {I18n.t('publisher.event_type_editor.sections.event_details.location.no_location')}
  </LocationSummaryContainer>;
const SelectedLocationSummary = ({
  locationConfigurations,
  isNotetakerEnabledForEt = false
}) => {
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker');
  const hasMultipleHost = Object.keys(locationConfigurations).length > 1;
  const flattenedLocations = Object.values(locationConfigurations).filter(member => !isEmpty(member)).flatMap(member => member).filter(Boolean);
  const uniqueLocations = hasMultipleHost ? flattenedLocations.reduce((acc, location) => {
    if (acc.some(accLocation => accLocation.kind === location.kind)) {
      return acc;
    }
    return [...acc, location];
  }, []) : flattenedLocations;
  const renderSingleLocation = () => <>
      <IconWrapper>
        <LocationIcon useNewIcons kind={uniqueLocations[0].kind} />
      </IconWrapper>
      <BodyText color={colorTextSecondary} inline>
        {infoForLocation(uniqueLocations[0], {
        isNotetakerEnabledForEt,
        hasMsTeamsFlag
      })}
      </BodyText>
    </>;
  const renderMultipleHostLocations = () => <>
      <IconWrapper>
        <LocationIcon kind="physical" />
      </IconWrapper>
      <BodyText color={colorTextSecondary} inline>
        {I18n.t('publisher.event_type_editor.sections.event_details.location.varies_by_host')}
      </BodyText>
    </>;

  // i18n-tasks-use t("publisher.event_type_editor.sections.event_details.location.collapsed_label.two")
  // i18n-tasks-use t("publisher.event_type_editor.sections.event_details.location.collapsed_label.three")
  // i18n-tasks-use t("publisher.event_type_editor.sections.event_details.location.collapsed_label.four_or_more")
  const getLocationLabel = count => {
    const locationLabels = uniqueLocations.map(location => infoForLocation(location, {
      isNotetakerEnabledForEt,
      hasMsTeamsFlag
    }));
    const [first, second, third] = locationLabels;
    const translations = {
      2: 'two',
      3: 'three',
      more: 'four_or_more'
    };
    const key = translations[count] || translations.more;
    const params = count > 3 ? {
      location_1: first,
      location_2: second,
      num_extra: locationLabels.length - 2
    } : {
      location_1: first,
      location_2: second,
      location_3: third
    };
    return I18n.t(`publisher.event_type_editor.sections.event_details.location.collapsed_label.${key}`, params);
  };
  const renderMultipleLocations = () => <>
      <IconWrapper>
        <LocationIcon useNewIcons kind={uniqueLocations[0].kind} />
      </IconWrapper>
      <BodyText color={colorTextSecondary} inline>
        {getLocationLabel(uniqueLocations.length)}
      </BodyText>
    </>;
  if (uniqueLocations.length === 1) return renderSingleLocation();
  if (hasMultipleHost && uniqueLocations.length > 1) return renderMultipleHostLocations();
  return renderMultipleLocations();
};
export const Summary = ({
  isReadOnly = false
}) => {
  const eventType = useEventType();
  const {
    values: {
      userPools,
      memberships,
      locationConfigurations,
      notetakerEnabledForEt
    }
  } = useFormContext();
  const {
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const enrichedLocationConfigurations = useLocationConfigurations({
    eventType,
    isRoundRobin,
    isCollectiveRoundRobin,
    userPools,
    memberships,
    locationConfigurations
  });
  const anyWithNoLocation = isEmpty(enrichedLocationConfigurations) || Object.values(enrichedLocationConfigurations).some(locations => isEmpty(locations));

  // Get the first location kind with an integration error
  const firstErrorLocation = Object.values(enrichedLocationConfigurations).flat().find(location => location.integrationError)?.kind || null;
  if (firstErrorLocation && !isReadOnly) {
    return <LocationErrorSummary kind={firstErrorLocation} />;
  }
  if (anyWithNoLocation && isReadOnly) return <NoLocationReadOnlySummary />;
  if (anyWithNoLocation) {
    return eventType.isUser ? <EmptyLocationSummaryUser /> : <EmptyLocationSummary />;
  }
  return <SelectedLocationSummary locationConfigurations={enrichedLocationConfigurations} isNotetakerEnabledForEt={Boolean(notetakerEnabledForEt)} />;
};

require("./summary.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./summary.js");